import * as React from "react";
import axios from "axios";
import {
  Await,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigation,
} from "react-router-dom";
import { GetLoanHistoryReturn } from "@tlc/ls2pac/dto/GetLoanHistoryReturn";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import {
  isLoanHistorySortType,
  LoanHistorySortType,
} from "./HostSystemItemSortControl";
import { getValidatedPage } from "./getValidatedPage";
import { SharedLoanHistoryView } from "./SharedLoanHistoryView";
import dayjs from "dayjs";
import { LSResultItemSkeleton } from "../search/ls/LSResultItem/LSResultItemSkeleton";
import { LSResultItemSkeletonList } from "../search/ls/LSResultItem/LSResultItemSkeletonList";
import { LSResultItemListWithToolbarSkeleton } from "../search/ls/LSResultItem/LSResultItemListWithToolbarSkeleton";
import { LSResultsSelectionProvider } from "../search/LSResultsSelectionContext";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";

export interface ChildHistoryLoaderData {
  id: string;
  page: number;
  sort: HostSystemItemSortType;
  pageSize: number;
  totalLoans: number;
  loanHistory: GetLoanHistoryReturn["loanHistory"];
}

export async function childHistoryLoader({
  params,
  request,
}: LoaderFunctionArgs) {
  const pageSize: number = 10;
  const id = params.id!;
  const searchParams = new URL(request.url).searchParams;
  const page = getValidatedPage(searchParams.get("page"));
  const sortParam = searchParams.get("sort");
  const sort =
    sortParam && isLoanHistorySortType(sortParam)
      ? sortParam
      : HostSystemItemSortType.OutDate;

  const start = (page - 1) * pageSize;

  const dataPromise = Promise.all([
    axios.get<{ count: number }>(`/loans/child/${id}/history/count`),
    axios.get<GetLoanHistoryReturn>(
      `/loans/child/${id}/history/${start}/${pageSize}/${sort}`,
    ),
  ])
    .then(([countResponse, historyResponse]) => ({
      id,
      page,
      sort,
      pageSize,
      totalLoans: countResponse.data.count,
      loanHistory: historyResponse.data.loanHistory,
    }))

    .catch((error) => {
      console.error("Error fetching child loan history", error);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Response("Forbidden", { status: 403 });
      }
      throw error;
    });

  return { data: dataPromise, pageSize: pageSize };
}

const fetchChildLoanHistoryRange = async (
  id: string,
  startDate?: Date,
  endDate?: Date,
): Promise<HostSystemItemView[]> => {
  if (!startDate || !endDate) return [];
  const { data } = await axios.post<GetLoanHistoryReturn>(
    `/loans/child/${id}/history/byDateRange`,
    {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
      includeResources: false,
    },
  );
  return data.loanHistory;
};

export const ChildHistoryView: React.FC = () => {
  const { data, pageSize } = useLoaderData() as {
    data: Promise<typeof childHistoryLoader>;
    pageSize: number;
  };

  const getId = React.useCallback(
    (item: ResourceView | ResourceDWR) => item.id,
    [],
  );

  return (
    <React.Suspense
      fallback={
        <LSResultItemListWithToolbarSkeleton
          pageSize={pageSize}
          hideCheckbox={true}
          hideActions={true}
        />
      }
    >
      <Await resolve={data}>
        {(resolved: ChildHistoryLoaderData) => {
          const items = resolved.loanHistory.flatMap((loan: any) =>
            loan.resource ? [loan.resource] : [],
          );
          return (
            <LSResultsSelectionProvider items={items} getId={getId}>
              <SharedLoanHistoryView
                page={resolved.page}
                pageSize={resolved.pageSize}
                sort={resolved.sort}
                totalLoans={resolved.totalLoans}
                displayedLoanHistory={resolved.loanHistory}
                emptyMessageKey="studentNoLoanHistoryMessage"
                requiresDateRange={true}
                isRateAndReviewEnabled={false}
                fetchPrintData={(startDate, endDate) =>
                  fetchChildLoanHistoryRange(resolved.id, startDate, endDate)
                }
                isStudent={true}
              />
            </LSResultsSelectionProvider>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
